import { Banner, Button, Dialog, Input, Select } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { funnelApi, type Deal, type Pipeline, type Stage } from '../../services/funnelApi';

type Props = {
  pipelines: Pipeline[];
  defaultPipelineId: string | null;
  onCreated: (deal: Deal) => void;
  onClose: () => void;
};

/** Modal de criação de negócio (título, valor, previsão de fechamento, funil e etapa). */
export function DealForm({ pipelines, defaultPipelineId, onCreated, onClose }: Props) {
  const [pipelineId, setPipelineId] = useState<string>(defaultPipelineId ?? pipelines[0]?.id ?? '');
  const [stageId, setStageId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [expectedCloseAt, setExpectedCloseAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Corrige o estado interno se o pipeline default mudou externamente
  useEffect(() => {
    if (!pipelineId && pipelines[0]) setPipelineId(pipelines[0].id);
  }, [pipelines]);

  const pipeline = pipelines.find((p) => p.id === pipelineId);
  const stages: Stage[] = pipeline?.stages ?? [];

  useEffect(() => {
    // Sempre que o pipeline muda, sincroniza stageId para o primeiro da lista
    if (!stageId || !stages.find((s) => s.id === stageId)) {
      setStageId(stages[0]?.id ?? '');
    }
  }, [pipelineId, stages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErr('Título é obrigatório');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const parsedAmount = amount ? Number(amount.replace(',', '.')) : null;
      const res = await funnelApi.createDeal({
        title: title.trim(),
        pipelineId: pipelineId || null,
        stageId: stageId || null,
        amount: Number.isFinite(parsedAmount) ? parsedAmount : null,
        expectedCloseAt: expectedCloseAt ? new Date(expectedCloseAt).toISOString() : null,
      });
      onCreated(res.deal);
    } catch (e: unknown) {
      setErr(e instanceof Error && e.message ? e.message : 'Erro ao criar negócio');
    } finally {
      setBusy(false);
    }
  };

  const pipelineItems = pipelines.map((p) => ({ value: p.id, label: p.name }));
  const stageItems = stages.map((st) => ({ value: st.id, label: st.name }));

  return (
    <Dialog.Root open={true} onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <Dialog size="base" className="p-6">
        <Dialog.Title className="mb-4 text-lg font-semibold">Novo negócio</Dialog.Title>
        <form onSubmit={handleSubmit} data-testid="funil-deal-form" className="flex flex-col gap-4">
          {err ? <Banner variant="error" description={<>{err}</>} /> : null}
          <Input label="Título" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Proposta anual" autoFocus data-testid="funil-form-title" />
          <Input label="Valor (R$)" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0,00" data-testid="funil-form-amount" />
          <Input label="Previsão de fechamento" type="date" value={expectedCloseAt} onChange={(e) => setExpectedCloseAt(e.target.value)} />
          {pipelines.length > 1 ? (
            <Select label="Funil" items={pipelineItems} value={pipelineId || null} onValueChange={(v: string | null) => { if (v) setPipelineId(v); }} />
          ) : null}
          {stageItems.length > 0 ? (
            <Select label="Etapa" items={stageItems} value={stageId || null} onValueChange={(v: string | null) => { if (v) setStageId(v); }} />
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={busy} data-testid="funil-form-submit">Criar negócio</Button>
          </div>
        </form>
      </Dialog>
    </Dialog.Root>
  );
}
